import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Bell, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useAuth } from '@/lib/AuthContext';

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const navigate = useNavigate();
  // Auth state comes from AuthContext — re-fetching the user here logged an
  // extra 401 in the console for signed-out visitors.
  const { user, isLoadingAuth } = useAuth();
  const isLoggedIn = !!user;

  useEffect(() => {
    if (isLoadingAuth) return;
    if (user?.square_customer_id) {
      setCustomer({ square_customer_id: user.square_customer_id });
    }
    loadCustomer();
  }, [user, isLoadingAuth]);

  useEffect(() => {
    if (customer?.square_customer_id) {
      // Subscribe to notification changes
      const unsubscribe = base44.entities.Notification.subscribe((event) => {
        if (event.type === 'create' && event.data?.customer_id === customer.square_customer_id) {
          loadNotifications();
        }
      });
      return unsubscribe;
    }
  }, [customer]);

  const loadCustomer = async () => {
    try {
      const customerData = localStorage.getItem('customer_data');
      if (customerData) {
        const data = JSON.parse(customerData);
        setCustomer(data);
        // Immediately load notifications for this customer
        if (data.square_customer_id) {
          await loadNotificationsForCustomer(data.square_customer_id);
        }
      }
    } catch (error) {
      console.error('Error loading customer:', error);
    }
  };

  const loadNotificationsForCustomer = async (customerId) => {
    setLoading(true);
    try {
      const data = await base44.entities.Notification.filter({
        customer_id: customerId
      }, '-created_date', 50);
      setNotifications(data);
      const unread = data.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    if (customer?.square_customer_id) {
      await loadNotificationsForCustomer(customer.square_customer_id);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await base44.entities.Notification.update(notificationId, { is_read: true });
      await loadNotifications();
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Failed to update notification');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await base44.entities.Notification.delete(notificationId);
      await loadNotifications();
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.link_value) {
      if (notification.link_type === 'internal_page') {
        window.location.href = `/${notification.link_value}`;
      } else {
        window.open(notification.link_value, '_blank');
      }
    }
  };

  // Always show the bell, but only show badge/count for logged-in users
  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => {
          if (!isLoggedIn && !customer?.square_customer_id) {
            // Redirect to login if not authenticated
            navigate('/login');
            return;
          }
          const isMobile = window.innerWidth < 1024;
          if (isMobile) {
            navigate(createPageUrl('NotificationCenter'));
          } else {
            setIsOpen(!isOpen);
          }
        }}
        className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6 text-black" />
        {(isLoggedIn || customer?.square_customer_id) && unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Desktop Notifications Dropdown */}
      {isOpen && (isLoggedIn || customer?.square_customer_id) && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Desktop Dropdown */}
          <div className="absolute top-12 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80 max-h-96 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-base">Notifications</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Notifications List */}
            <div className="divide-y">
              {loading ? (
                <div className="p-4 text-center text-gray-500 text-sm">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                notifications.map(notif => (
                  <div
                    key={notif.id}
                    className={`p-3 cursor-pointer transition-colors ${
                      notif.is_read ? 'bg-white' : 'bg-blue-50'
                    } hover:bg-gray-50`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    {notif.image_url && (
                       <img
                         src={notif.image_url}
                         alt={notif.title}
                         className="w-full h-40 object-cover rounded mb-2"
                       />
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-900">{notif.title}</p>
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notif.created_date).toLocaleDateString()}
                        </p>
                      </div>
                      {!notif.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {notif.link_value && (
                        <button
                          className="flex-1 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notif);
                          }}
                        >
                          Open
                        </button>
                      )}
                      <button
                        className="px-2 py-1 hover:bg-red-100 rounded transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notif.id);
                        }}
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}